import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';

import Paper from '@material-ui/core/Paper';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Snackbar from '@material-ui/core/Snackbar';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';

import { fetchVideos, loadMoreVideos } from '../actions';
import { copyEmbedToClipboard, getFooterState } from '../utils';
import { DM_ALL_CHANNELS, DM_CHANNELS, DM_DEFAULT_SORT, DM_SORTS } from '../constants';
import VideoCard from './VideoCard';

const SEARCH_DEBOUNCE_MS = 400;
const SNACK_DURATION_MS = 2500;

const styles = theme => ({
  headerBar: {
    marginBottom: theme.spacing(2),
  },
  toolbar: {
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  searchField: {
    flexGrow: 1,
    minWidth: 320,
  },
  sortControl: {
    minWidth: 150,
  },
  channelControl: {
    minWidth: 170,
  },
  tagsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    padding: theme.spacing(0, 2, 1.5),
  },
  tag: {
    cursor: 'pointer',
  },
  grid: {
    marginTop: 0,
  },
  centered: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    padding: theme.spacing(6, 2),
    color: theme.palette.text.secondary,
  },
  loadMore: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3, 0),
  },
  errorBanner: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
    padding: theme.spacing(1, 2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
});

/**
 * Shared, connected video browser: search + channel filter + sort + optional
 * player tags + card grid + "load more". Reused by the standalone page (mode
 * "copy") and by the in-article insert modal (mode "select").
 *
 * Props:
 *  - mode: "copy" (default) | "select"
 *  - onSelect(videoId): called on card click in "select" mode
 *  - players: optional [{ id, label }] rendered as clickable tags below search
 *  - titleKey / showTitle: toolbar title control
 */
@injectIntl
@withStyles(styles)
@connect(store => ({
  videos: store.videos.list,
  fetching: store.videos.fetching,
  fetchingMore: store.videos.fetchingMore,
  hasMore: store.videos.hasMore,
  page: store.videos.page,
  error: store.videos.error,
}))
export default class VideoBrowser extends Component {
  state = {
    search: '',
    sort: DM_DEFAULT_SORT,
    channel: DM_ALL_CHANNELS,
    snackOpen: false,
    snackMessage: '',
  };

  searchTimer = null;

  componentDidMount() {
    const { sort, channel } = this.state;
    this.props.dispatch(fetchVideos('', sort, channel));
  }

  componentWillUnmount() {
    clearTimeout(this.searchTimer);
  }

  triggerFetch = () => {
    const { search, sort, channel } = this.state;
    this.props.dispatch(fetchVideos(search.trim(), sort, channel));
  };

  handleSearchChange = event => {
    const search = event.target.value;
    this.setState({ search });
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(this.triggerFetch, SEARCH_DEBOUNCE_MS);
  };

  setSearch = value => {
    clearTimeout(this.searchTimer);
    this.setState({ search: value }, this.triggerFetch);
  };

  handleClearSearch = () => this.setSearch('');

  handleSortChange = event => {
    this.setState({ sort: event.target.value }, this.triggerFetch);
  };

  handleChannelChange = event => {
    clearTimeout(this.searchTimer);
    this.setState({ channel: event.target.value }, this.triggerFetch);
  };

  handleLoadMore = () => {
    const { search, sort, channel } = this.state;
    const { dispatch, page } = this.props;
    dispatch(loadMoreVideos(search.trim(), sort, page + 1, channel));
  };

  handleCopyEmbed = async videoId => {
    const { messages: m } = this.props.intl;
    try {
      await copyEmbedToClipboard(videoId);
      this.setState({ snackOpen: true, snackMessage: m.videos_embedCopied });
    } catch (e) {
      this.setState({ snackOpen: true, snackMessage: m.videos_embedCopyError });
    }
  };

  handleSnackClose = () => this.setState({ snackOpen: false });

  renderTags() {
    const { players, classes } = this.props;
    if (!players || !players.length) {
      return null;
    }
    return (
      <div className={classes.tagsRow} data-test="VideoBrowser__tags">
        {players.map(player => (
          <Chip
            key={player.id}
            label={player.label}
            size="small"
            clickable
            onClick={() => this.setSearch(player.label)}
            className={classes.tag}
            data-test="VideoBrowser__playerTag"
          />
        ))}
      </div>
    );
  }

  renderBody() {
    const { videos, fetching, error, classes, intl, mode, onSelect } = this.props;
    const { search } = this.state;
    const { messages: m } = intl;

    if (error && videos.length === 0) {
      return (
        <div className={classes.errorBanner} data-test="VideoBrowser__error">
          <span>{error === 429 ? m.videos_errorRateLimit : m.videos_error}</span>
          <Button color="inherit" size="small" onClick={this.triggerFetch}>
            {m.videos_retry}
          </Button>
        </div>
      );
    }

    if (fetching && videos.length === 0) {
      return (
        <div className={classes.centered} data-test="VideoBrowser__loading">
          <CircularProgress />
        </div>
      );
    }

    if (!fetching && videos.length === 0) {
      return (
        <div className={classes.centered} data-test="VideoBrowser__empty">
          <Typography variant="h6" color="inherit">
            {m.videos_noResults}
          </Typography>
          {search && (
            <Button color="primary" onClick={this.handleClearSearch}>
              {m.videos_clearSearch}
            </Button>
          )}
        </div>
      );
    }

    return (
      <Grid container spacing={2} className={classes.grid} data-test="VideoBrowser__grid">
        {videos.map(video => (
          <Grid item key={video.id} xs={12} sm={6} md={4} lg={3}>
            <VideoCard
              video={video}
              messages={m}
              onCopyEmbed={mode === 'select' ? undefined : this.handleCopyEmbed}
              onSelect={mode === 'select' ? onSelect : undefined}
            />
          </Grid>
        ))}
      </Grid>
    );
  }

  renderFooter() {
    const { hasMore, fetchingMore, fetching, page, videos, classes, intl } = this.props;
    const { messages: m } = intl;

    const footer = getFooterState(videos.length, page, hasMore);
    if (footer === 'none') {
      return null;
    }
    if (footer === 'refine' || footer === 'end') {
      return (
        <div className={classes.loadMore} data-test="VideoBrowser__footerMessage">
          <Typography variant="body2" color="textSecondary">
            {footer === 'refine' ? m.videos_maxReached : m.videos_endOfResults}
          </Typography>
        </div>
      );
    }
    return (
      <div className={classes.loadMore}>
        <Button
          variant="outlined"
          color="primary"
          onClick={this.handleLoadMore}
          disabled={fetchingMore || fetching}
          data-test="VideoBrowser__loadMore"
        >
          {fetchingMore ? <CircularProgress size={20} /> : m.videos_loadMore}
        </Button>
      </div>
    );
  }

  render() {
    const { classes, intl, mode, showTitle, titleKey } = this.props;
    const { search, sort, channel, snackOpen, snackMessage } = this.state;
    const { messages: m } = intl;

    return (
      <React.Fragment>
        <Paper elevation={2} className={classes.headerBar}>
          <Toolbar className={classes.toolbar}>
            {showTitle !== false && <Typography variant="h6">{m[titleKey || 'videos_pageTitle']}</Typography>}
            <TextField
              className={classes.searchField}
              placeholder={m.videos_searchPlaceholder}
              value={search}
              onChange={this.handleSearchChange}
              data-test="VideoBrowser__search"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={this.handleClearSearch} aria-label={m.videos_clearSearch}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            <FormControl className={classes.channelControl}>
              <Select value={channel} onChange={this.handleChannelChange} displayEmpty data-test="VideoBrowser__channel">
                <MenuItem value={DM_ALL_CHANNELS}>{m.videos_channel_all}</MenuItem>
                {DM_CHANNELS.map(option => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl className={classes.sortControl}>
              <Select value={sort} onChange={this.handleSortChange} data-test="VideoBrowser__sort">
                {DM_SORTS.map(option => (
                  <MenuItem key={option} value={option}>
                    {m[`videos_sort_${option}`]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Toolbar>
          {this.renderTags()}
        </Paper>

        {this.renderBody()}
        {this.renderFooter()}

        {mode !== 'select' && (
          <Snackbar
            open={snackOpen}
            autoHideDuration={SNACK_DURATION_MS}
            onClose={this.handleSnackClose}
            message={snackMessage}
            ContentProps={{ 'data-test': 'VideoBrowser__snackbar' }}
          />
        )}
      </React.Fragment>
    );
  }
}
